import { useState, useEffect, useCallback } from 'react'
import { ImageUploader } from './ImageUploader'
import { EntityLinker } from './EntityLinker'
import { GenerateButton, Icon, VoiceSuggestionButton } from '../common'
import { useProject } from '../../context/ProjectContext'
import { useProjectEntities } from '../../hooks/useProjectEntities'

/**
 * EntityForm - Dynamic form for creating/editing entities
 * 
 * @param {string} entityType - 'character' | 'location' | 'event' | 'shot'
 * @param {string} mode - 'create' | 'edit'
 * @param {object} initialData - Data to populate form when editing
 * @param {function} onSave - Save handler receives form data
 * @param {function} onCancel - Cancel handler
 * @param {function} onDelete - Delete handler (edit mode only)
 * @param {string} accent - 'cyan' | 'amber'
 * @param {boolean} canEdit - Whether user has permission to edit (default: true)
 * @param {boolean} canDelete - Whether user has permission to delete (default: true)
 */
export function EntityForm({
  entityType,
  mode,
  initialData,
  onSave,
  onCancel,
  onDelete,
  accent = 'cyan',
  canEdit = true,
  canDelete = true,
}) {
  // Determine if form is read-only based on mode and permissions
  const isReadOnly = mode === 'edit' && !canEdit
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  
  // Get project context for AI generation
  const { projectId } = useProject()
  const { getAIContext } = useProjectEntities(projectId)
  const projectContext = getAIContext()

  // Initialize form data based on entity type
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData(initialData)
    } else {
      setFormData(getDefaultData(entityType))
    }
  }, [entityType, mode, initialData])

  // Get default data structure for entity type
  function getDefaultData(type) {
    const base = {
      name: '',
      description: '',
      primaryImage: '',
      extraImages: [],
      linkedCharacters: [],
      linkedLocations: [],
      linkedEvents: [],
      linkedShots: [],
      tier: 'B', // Default tier: A = Priority, B = Standard, C = Archive
    }

    switch (type) {
      case 'character':
        return {
          ...base,
          attire: '',
          hairStyle: '',
          distinctFeatures: '',
          accessories: '',
          voice: null, // { voiceId, voiceName, stability, similarityBoost, style, notes }
        }
      case 'location':
        return {
          ...base,
          era: '',
          keyElements: '',
          atmosphereNotes: '',
        }
      case 'event':
        return {
          ...base,
          title: '',
          date: '',
          era: '',
          showOnTimeline: true,
        }
      case 'shot':
        return {
          ...base,
          shotId: '',
          title: '',
          durationSeconds: 0,
          startFrame: '',
          endFrame: '',
          layer: 'A',
          aspectRatio: '16:9',
        }
      default:
        return base
    }
  }

  // Update single field
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }, [errors])

  // Handle primary image change (store full image data including publicId)
  const handlePrimaryImageChange = useCallback((imageData) => {
    setFormData(prev => ({ 
      ...prev, 
      // Store as object with url and publicId for deletion capability
      primaryImage: imageData ? { 
        url: imageData.url, 
        publicId: imageData.publicId,
        path: imageData.path,
      } : '',
      primaryImageData: imageData || null,
    }))
  }, [])

  // Handle extra images change (store full image data including publicId)
  const handleExtraImagesChange = useCallback((images) => {
    setFormData(prev => ({
      ...prev,
      // Store as array of objects with url and publicId for deletion capability
      extraImages: images.map(img => ({
        url: img.url || (typeof img === 'string' ? img : ''),
        publicId: img.publicId || null,
        path: img.path || null,
      })).filter(img => img.url),
      extraImagesData: images,
    }))
  }, [])

  // Handle linked entities change
  const handleLinksChange = useCallback((linkType, ids) => {
    setFormData(prev => ({ ...prev, [linkType]: ids }))
  }, [])

  // Handle voice selection from suggestions
  const handleVoiceSelect = useCallback((voiceSuggestion) => {
    setFormData(prev => ({
      ...prev,
      voice: {
        voiceId: voiceSuggestion.voiceId,
        voiceName: voiceSuggestion.voiceName,
        stability: voiceSuggestion.recommendedSettings.stability,
        similarityBoost: voiceSuggestion.recommendedSettings.similarityBoost,
        style: voiceSuggestion.recommendedSettings.style,
        notes: voiceSuggestion.matchReason,
      }
    }))
  }, [])

  // Validate form
  const validate = () => {
    const newErrors = {}
    const nameField = entityType === 'event' || entityType === 'shot' ? 'title' : 'name'
    
    if (!formData[nameField]?.trim()) {
      newErrors[nameField] = `${nameField === 'title' ? 'Title' : 'Name'} is required`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Sanitize form data before saving to Firestore
  // Removes temporary/non-serializable fields like File objects and blob URLs
  const sanitizeForFirestore = (data) => {
    // Start with a copy excluding temporary image data fields
    const { primaryImageData, extraImagesData, ...cleanData } = data
    
    // Ensure primaryImage is clean (no file objects)
    if (cleanData.primaryImage && typeof cleanData.primaryImage === 'object') {
      cleanData.primaryImage = {
        url: cleanData.primaryImage.url || '',
        publicId: cleanData.primaryImage.publicId || null,
        path: cleanData.primaryImage.path || null,
      }
    }
    
    // Ensure extraImages array is clean (no file objects or blob URLs)
    if (cleanData.extraImages && Array.isArray(cleanData.extraImages)) {
      cleanData.extraImages = cleanData.extraImages.map(img => {
        if (typeof img === 'string') return img
        return {
          url: img.url || '',
          publicId: img.publicId || null,
          path: img.path || null,
        }
      }).filter(img => typeof img === 'string' ? img : img.url)
    }
    
    return cleanData
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validate()) return

    setSaving(true)
    try {
      // Sanitize data to remove File objects and other non-serializable fields
      const cleanData = sanitizeForFirestore(formData)
      await onSave(cleanData)
    } catch (err) {
      console.error('Save error:', err)
      setErrors({ submit: err.message || 'Failed to save' })
    } finally {
      setSaving(false)
    }
  }

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete this ${entityType}?`)) {
      return
    }
    
    setSaving(true)
    try {
      await onDelete()
    } catch (err) {
      console.error('Delete error:', err)
      setErrors({ submit: err.message || 'Failed to delete' })
    } finally {
      setSaving(false)
    }
  }

  // Get name/title field based on entity type
  const nameField = entityType === 'event' || entityType === 'shot' ? 'title' : 'name'

  return (
    <form 
      className={`entity-form entity-form--${accent} ${isReadOnly ? 'entity-form--readonly' : ''}`} 
      onSubmit={handleSubmit}
    >
      {/* Read-only indicator */}
      {isReadOnly && (
        <div className="entity-form__readonly-banner">
                <span className="entity-form__readonly-icon"><Icon name="eye" size={16} /></span>
          <span>View only — you don't have permission to edit this {entityType}</span>
        </div>
      )}

      {/* Primary Info Section */}
      <section className="entity-form__section">
        <h3 className="entity-form__section-title">Basic Info</h3>
        
        <div className="entity-form__row">
          {/* Left: Image Upload */}
          <div className="entity-form__field">
            <label className="entity-form__label">Primary Image</label>
            <ImageUploader
              value={formData.primaryImage}
              onChange={handlePrimaryImageChange}
              entityType={entityType}
              accent={accent}
            />
          </div>

          {/* Right: Name/Title + Description */}
          <div className="entity-form__field" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div className="entity-form__field">
              <div className="entity-form__label-row">
                <label className="entity-form__label entity-form__label--required">
                  {nameField === 'title' ? 'Title' : 'Name'}
                </label>
                <GenerateButton
                  fieldLabel={nameField === 'title' ? 'Title' : 'Name'}
                  fieldKey={nameField}
                  entityType={entityType}
                  entity={formData}
                  onApply={(text) => handleChange(nameField, text)}
                  projectContext={projectContext}
                />
              </div>
              <input
                type="text"
                className="entity-form__input"
                value={formData[nameField] || ''}
                onChange={(e) => handleChange(nameField, e.target.value)}
                placeholder={`Enter ${entityType} ${nameField}`}
                disabled={isReadOnly}
              />
              {errors[nameField] && (
                <span className="entity-form__error">{errors[nameField]}</span>
              )}
            </div>

            <div className="entity-form__field">
              <div className="entity-form__label-row">
                <label className="entity-form__label">Description</label>
                <GenerateButton
                  fieldLabel="Description"
                  fieldKey="description"
                  entityType={entityType}
                  entity={formData}
                  onApply={(text) => handleChange('description', text)}
                  projectContext={projectContext}
                />
              </div>
              <textarea
                className="entity-form__textarea"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={`Describe this ${entityType}...`}
                rows={4}
                disabled={isReadOnly}
              />
            </div>

            <div className="entity-form__field">
              <label className="entity-form__label">Tier</label>
              <select
                className="entity-form__select"
                value={formData.tier || 'B'}
                onChange={(e) => handleChange('tier', e.target.value)}
                disabled={isReadOnly}
              >
                <option value="A">Tier A (Priority)</option>
                <option value="B">Tier B (Standard)</option>
                <option value="C">Tier C (Archive)</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Type-specific fields */}
      <section className="entity-form__section">
        <h3 className="entity-form__section-title">{entityType} Details</h3>
        
        {entityType === 'character' && (
          <>
            <div className="entity-form__row">
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Attire</label>
                  <GenerateButton
                    fieldLabel="Attire"
                    fieldKey="attire"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('attire', text)}
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.attire || ''}
                  onChange={(e) => handleChange('attire', e.target.value)}
                  placeholder="e.g., Vintage 1980s clothes"
                />
              </div>
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Hair Style</label>
                  <GenerateButton
                    fieldLabel="Hair Style"
                    fieldKey="hairStyle"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('hairStyle', text)}
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.hairStyle || ''}
                  onChange={(e) => handleChange('hairStyle', e.target.value)}
                  placeholder="e.g., Messy brown hair"
                />
              </div>
            </div>
            <div className="entity-form__row">
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Distinct Features</label>
                  <GenerateButton
                    fieldLabel="Distinct Features"
                    fieldKey="distinctFeatures"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('distinctFeatures', text)}
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.distinctFeatures || ''}
                  onChange={(e) => handleChange('distinctFeatures', e.target.value)}
                  placeholder="e.g., Scar on left cheek"
                />
              </div>
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Accessories</label>
                  <GenerateButton
                    fieldLabel="Accessories"
                    fieldKey="accessories"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('accessories', text)}
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.accessories || ''}
                  onChange={(e) => handleChange('accessories', e.target.value)}
                  placeholder="e.g., Vintage camera, watch"
                />
              </div>
            </div>

            {/* Voice Section */}
            <div className="entity-form__row">
              <div className="entity-form__field entity-form__field--full">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Voice</label>
                  <VoiceSuggestionButton
                    character={formData}
                    onSelectVoice={handleVoiceSelect}
                  />
                </div>
                {formData.voice ? (
                  <div className="entity-form__voice-preview">
                    <div className="entity-form__voice-info">
                      <span className="entity-form__voice-name">{formData.voice.voiceName}</span>
                      <span className="entity-form__voice-id">{formData.voice.voiceId}</span>
                    </div>
                    <div className="entity-form__voice-settings">
                      <span>Stability: {Math.round((formData.voice.stability || 0.5) * 100)}%</span>
                      <span>Clarity: {Math.round((formData.voice.similarityBoost || 0.75) * 100)}%</span>
                    </div>
                    {formData.voice.notes && (
                      <p className="entity-form__voice-notes">{formData.voice.notes}</p>
                    )}
                    <button
                      type="button"
                      className="entity-form__voice-clear"
                      onClick={() => handleChange('voice', null)}
                      disabled={isReadOnly}
                    >
                      <Icon name="close" size={12} />
                      Clear voice
                    </button>
                  </div>
                ) : (
                  <p className="entity-form__voice-empty">
                    No voice selected. Add character details and click "Suggest Voice" to get recommendations.
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {entityType === 'location' && (
          <>
            <div className="entity-form__row">
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Era</label>
                  <GenerateButton
                    fieldLabel="Era"
                    fieldKey="era"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('era', text)}
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.era || ''}
                  onChange={(e) => handleChange('era', e.target.value)}
                  placeholder="e.g., Victorian Era, 1920s, Medieval"
                />
              </div>
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Key Elements</label>
                  <GenerateButton
                    fieldLabel="Key Elements"
                    fieldKey="keyElements"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('keyElements', text)}
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.keyElements || ''}
                  onChange={(e) => handleChange('keyElements', e.target.value)}
                  placeholder="e.g., Neon signs, palm trees"
                />
              </div>
            </div>
            <div className="entity-form__field">
              <div className="entity-form__label-row">
                <label className="entity-form__label">Atmosphere Notes</label>
                <GenerateButton
                  fieldLabel="Atmosphere Notes"
                  fieldKey="atmosphereNotes"
                  entityType={entityType}
                  entity={formData}
                  onApply={(text) => handleChange('atmosphereNotes', text)}
                  projectContext={projectContext}
                />
              </div>
              <textarea
                className="entity-form__textarea"
                value={formData.atmosphereNotes || ''}
                onChange={(e) => handleChange('atmosphereNotes', e.target.value)}
                placeholder="Describe the mood, lighting, ambiance..."
                rows={3}
              />
            </div>
          </>
        )}

        {entityType === 'event' && (
          <>
            <div className="entity-form__row">
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Date</label>
                  <GenerateButton
                    fieldLabel="Date"
                    fieldKey="date"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('date', text)}
                    extraContext="Prefer an ISO-like date string if possible (e.g., 1985-07-04) or a clear historical label (e.g., 2560 BCE)."
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.date || ''}
                  onChange={(e) => handleChange('date', e.target.value)}
                  placeholder="e.g., November 5, 1955, 2560 BCE"
                />
              </div>
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Era</label>
                  <GenerateButton
                    fieldLabel="Era"
                    fieldKey="era"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('era', text)}
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.era || ''}
                  onChange={(e) => handleChange('era', e.target.value)}
                  placeholder="e.g., Ancient, Modern, Future"
                />
              </div>
            </div>
            <div className="entity-form__field">
              <label className="entity-form__toggle">
                <input
                  type="checkbox"
                  className="entity-form__toggle-input"
                  checked={formData.showOnTimeline ?? true}
                  onChange={(e) => handleChange('showOnTimeline', e.target.checked)}
                />
                <span className="entity-form__label">Show on Timeline</span>
              </label>
            </div>
          </>
        )}

        {entityType === 'shot' && (
          <>
            <div className="entity-form__row">
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Shot ID</label>
                  <GenerateButton
                    fieldLabel="Shot ID"
                    fieldKey="shotId"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('shotId', text)}
                    extraContext="Use a concise ID like EP01_S01_A (episode + scene + layer)."
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.shotId || ''}
                  onChange={(e) => handleChange('shotId', e.target.value)}
                  placeholder="e.g., EP01_S01_A"
                />
              </div>
              <div className="entity-form__field">
                <label className="entity-form__label">Duration (seconds)</label>
                <input
                  type="number"
                  className="entity-form__input"
                  value={formData.durationSeconds || ''}
                  onChange={(e) => handleChange('durationSeconds', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="0.1"
                />
              </div>
            </div>
            <div className="entity-form__row">
              <div className="entity-form__field">
                <label className="entity-form__label">Layer</label>
                <select
                  className="entity-form__select"
                  value={formData.layer || 'A'}
                  onChange={(e) => handleChange('layer', e.target.value)}
                >
                  <option value="A">Layer A (Primary)</option>
                  <option value="B">Layer B (Secondary)</option>
                  <option value="C">Layer C (Tertiary)</option>
                  <option value="D">Layer D (Background)</option>
                </select>
              </div>
              <div className="entity-form__field">
                <label className="entity-form__label">Aspect Ratio</label>
                <select
                  className="entity-form__select"
                  value={formData.aspectRatio || '16:9'}
                  onChange={(e) => handleChange('aspectRatio', e.target.value)}
                >
                  <option value="16:9">16:9 (Widescreen)</option>
                  <option value="4:3">4:3 (Standard)</option>
                  <option value="1:1">1:1 (Square)</option>
                  <option value="9:16">9:16 (Portrait)</option>
                </select>
              </div>
            </div>
            <div className="entity-form__row">
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">Start Frame</label>
                  <GenerateButton
                    fieldLabel="Start Frame"
                    fieldKey="startFrame"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('startFrame', text)}
                    extraContext="Describe the opening frame clearly (camera, subject, setting, vibe)."
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.startFrame || ''}
                  onChange={(e) => handleChange('startFrame', e.target.value)}
                  placeholder="Frame description or number"
                />
              </div>
              <div className="entity-form__field">
                <div className="entity-form__label-row">
                  <label className="entity-form__label">End Frame</label>
                  <GenerateButton
                    fieldLabel="End Frame"
                    fieldKey="endFrame"
                    entityType={entityType}
                    entity={formData}
                    onApply={(text) => handleChange('endFrame', text)}
                    extraContext="Describe the closing frame clearly (camera, subject, setting, vibe)."
                    projectContext={projectContext}
                  />
                </div>
                <input
                  type="text"
                  className="entity-form__input"
                  value={formData.endFrame || ''}
                  onChange={(e) => handleChange('endFrame', e.target.value)}
                  placeholder="Frame description or number"
                />
              </div>
            </div>
          </>
        )}
      </section>

      {/* Extra Images */}
      <section className="entity-form__section">
        <h3 className="entity-form__section-title">Additional Images</h3>
        <ImageUploader
          value={formData.extraImages || []}
          onChange={handleExtraImagesChange}
          entityType={entityType}
          accent={accent}
          multiple
        />
      </section>

      {/* Entity Links */}
      <section className="entity-form__section">
        <h3 className="entity-form__section-title">Linked Entities</h3>
        
        <div className="entity-form__row">
          {entityType !== 'character' && (
            <EntityLinker
              label="Characters"
              linkType="linkedCharacters"
              entityType="characters"
              value={formData.linkedCharacters || []}
              onChange={(ids) => handleLinksChange('linkedCharacters', ids)}
              accent={accent}
            />
          )}
          {entityType !== 'location' && (
            <EntityLinker
              label="Locations"
              linkType="linkedLocations"
              entityType="locations"
              value={formData.linkedLocations || []}
              onChange={(ids) => handleLinksChange('linkedLocations', ids)}
              accent={accent}
            />
          )}
        </div>
        
        <div className="entity-form__row">
          {entityType !== 'event' && (
            <EntityLinker
              label="Events"
              linkType="linkedEvents"
              entityType="events"
              value={formData.linkedEvents || []}
              onChange={(ids) => handleLinksChange('linkedEvents', ids)}
              accent={accent}
            />
          )}
          {entityType !== 'shot' && (
            <EntityLinker
              label="Shots"
              linkType="linkedShots"
              entityType="shots"
              value={formData.linkedShots || []}
              onChange={(ids) => handleLinksChange('linkedShots', ids)}
              accent={accent}
            />
          )}
        </div>
      </section>

      {/* Error message */}
      {errors.submit && (
        <div className="entity-form__error-banner">
          {errors.submit}
        </div>
      )}

      {/* Actions */}
      <div className="entity-form__actions">
        <div className="entity-form__actions-left">
          {mode === 'edit' && onDelete && canDelete && (
            <button
              type="button"
              className="entity-form__btn entity-form__btn--danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}
        </div>
        
        <div className="entity-form__actions-right">
          <button
            type="button"
            className="entity-form__btn entity-form__btn--secondary"
            onClick={onCancel}
            disabled={saving}
          >
            {isReadOnly ? 'Close' : 'Cancel'}
          </button>
          {!isReadOnly && (
            <button
              type="submit"
              className="entity-form__btn entity-form__btn--primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </form>
  )
}

export default EntityForm

